import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft01Icon,
  ArrowRight01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { cx } from "shared/utils";
import boomLogo from "shared/branding/logo.svg";
import {
  useSurveyStore,
  selectSortedQuestions,
  selectConditions
} from "../../store";
import { Toolbar } from "./toolbar";
import { ProgressBar } from "./progress-bar";
import { QuestionInput } from "./question-input";
import { SlideTransition } from "./slide-transition";
import { evaluateConditions } from "./conditions";
import { isAutoAdvanceType } from "./types";
import type { ViewMode, Answers } from "./types";
import { StartSurveySlice } from "views/land-x-token/slices/StartSurvey";
import { EndSurveySlice } from "views/land-x-token/slices/EndSurvey";
import { DraftSurveyTemplateQuestion } from "views/landlord-surveys-id/types";

type PreviewPhase = "start" | "questionnaire" | "end";

export const PreviewModal = ({ onClose }: { onClose: () => void }) => {
  const allQuestions = useSurveyStore(selectSortedQuestions);
  const conditions = useSurveyStore(selectConditions);

  const [viewMode, setViewMode] = useState<ViewMode>("desktop");
  const [phase, setPhase] = useState<PreviewPhase>("start");
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const directionRef = useRef<"left" | "right">("right");

  const { visibleQuestions, endSurveyAfterIds } = useMemo(
    () => evaluateConditions(allQuestions, conditions, answers),
    [allQuestions, conditions, answers]
  );

  const totalQuestions = visibleQuestions.length;

  // When conditions hide all remaining questions, treat as end of survey
  useEffect(() => {
    if (
      phase === "questionnaire" &&
      (totalQuestions === 0 || currentIdx >= totalQuestions)
    )
      setPhase("end");
  }, [totalQuestions, currentIdx, phase]);

  const currentQuestion: DraftSurveyTemplateQuestion | null =
    visibleQuestions[currentIdx] ?? null;
  const currentAnswer = currentQuestion
    ? answers[currentQuestion.internal_id]
    : undefined;
  const isMobile = viewMode === "mobile";

  const canGoNext = (() => {
    if (!currentQuestion) return false;
    if (!currentQuestion.is_required) return true;
    if (!currentAnswer) return false;
    if (Array.isArray(currentAnswer)) return currentAnswer.length > 0;
    return currentAnswer.length > 0;
  })();

  const restart = useCallback(() => {
    directionRef.current = "right";
    setPhase("start");
    setCurrentIdx(0);
    setAnswers({});
  }, []);

  const goNext = useCallback(() => {
    directionRef.current = "right";
    if (phase === "start") {
      setPhase("questionnaire");
      return;
    }
    const shouldEnd = endSurveyAfterIds.has(currentQuestion.internal_id);
    if (shouldEnd || currentIdx >= totalQuestions - 1) setPhase("end");
    else setCurrentIdx((i) => i + 1);
  }, [phase, currentIdx, totalQuestions, currentQuestion, endSurveyAfterIds]);

  const goBack = useCallback(() => {
    directionRef.current = "left";
    if (phase === "end") setPhase("questionnaire");
    else if (phase === "questionnaire" && currentIdx === 0) setPhase("start");
    else if (currentIdx > 0) setCurrentIdx((i) => i - 1);
  }, [phase, currentIdx]);

  const handleAnswer = useCallback(
    (value: string | string[]) => {
      setAnswers((prev) => ({ ...prev, [currentQuestion.internal_id]: value }));
      if (isAutoAdvanceType(currentQuestion.type)) goNext();
    },
    [currentQuestion, goNext]
  );

  const renderContent = () => {
    if (phase === "start")
      return (
        <div className="h-full overflow-hidden">
          <StartSurveySlice
            onContinue={goNext}
            mockLandlordInfo={{
              name: "Landlord name",
              address: "Landlord address"
            }}
            compact={isMobile}
          />
        </div>
      );

    if (phase === "end")
      return (
        <div className="h-full overflow-hidden">
          <EndSurveySlice />
        </div>
      );

    return (
      <div className="flex flex-col h-full">
        <div className={cx("shrink-0", isMobile ? "px-5 py-4" : "px-6 py-4")}>
          <img src={boomLogo} alt="Boom" height={30} />
        </div>

        <ProgressBar current={currentIdx} total={totalQuestions} />

        <form
          className="flex flex-col flex-grow overflow-hidden"
          onSubmit={(e) => {
            e.preventDefault();
            if (canGoNext) goNext();
          }}
        >
          <div
            className={cx(
              "flex-grow overflow-hidden bg-background-900 relative",
              isMobile ? "px-5 py-6" : "px-8 py-10"
            )}
          >
            <div
              className={
                isMobile ? "h-full" : "max-w-[500px] mx-auto w-full h-full"
              }
            >
              <SlideTransition
                transitionKey={currentIdx}
                direction={directionRef.current}
              >
                <div className="overflow-y-auto h-full">
                  <h2
                    className={cx(
                      "font-bold text-content-1000 mb-2",
                      isMobile ? "text-xl" : "text-2xl"
                    )}
                  >
                    {currentQuestion?.question || "Untitled question"}
                  </h2>
                  <Show if={currentQuestion.description}>
                    <p
                      className={cx(
                        isMobile ? "text-sm" : "text-base",
                        "text-sm font-medium text-content-400 mb-6"
                      )}
                    >
                      {currentQuestion.description}
                    </p>
                  </Show>
                  {!currentQuestion.description && <div className="mb-6" />}
                  <QuestionInput
                    question={currentQuestion}
                    answer={currentAnswer}
                    onAnswer={handleAnswer}
                  />
                </div>
              </SlideTransition>
            </div>
          </div>

          {totalQuestions > 0 && (
            <div
              className={cx(
                "shrink-0 border-t border-alpha-80",
                isMobile ? "px-5 py-4 flex gap-3" : "px-8 py-4"
              )}
            >
              <div
                className={cx(
                  "flex gap-3",
                  isMobile ? "w-full" : "max-w-[500px] mx-auto w-full"
                )}
              >
                <Button
                  size={ButtonSize.SM}
                  variant={ButtonVariant.BASE}
                  HugeIcon={ArrowLeft01Icon}
                  onClick={goBack}
                  className={isMobile ? "w-[100px] shrink-0" : "flex-1"}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  size={ButtonSize.SM}
                  variant={ButtonVariant.PRIMARY}
                  HugeIcon={ArrowRight01Icon}
                  iconPosition="right"
                  className="flex-1 w-full"
                >
                  {currentIdx === totalQuestions - 1 ||
                  endSurveyAfterIds.has(currentQuestion.internal_id)
                    ? "Submit"
                    : "Next"}
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-white">
      <Toolbar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onRestart={restart}
        onClose={onClose}
      />

      <div className="flex-grow overflow-hidden bg-background-900 flex items-start justify-center">
        <div
          className={cx(
            "flex flex-col transition-all duration-300",
            isMobile
              ? "w-[375px] my-6 h-[calc(100%-48px)] max-h-[700px] rounded-2xl border border-alpha-100 shadow-lg bg-white overflow-hidden"
              : "w-full h-full bg-white"
          )}
        >
          {renderContent()}
        </div>
      </div>
    </div>,
    document.body
  );
};
